import { mergeClasses } from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";
import { getTemplates } from "api";
import { QueryCacheConfig } from "common/constants";
import { ITemplate, ITemplateGalleryModalProps } from "models/ITemplate";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import { useQuickStartGalleryStyles } from "./quickStartGallery.styles";
import { TemplateDetail } from "./TemplateDetail";
import { TemplateListItem } from "./TemplateListItem";

export const TemplateGalleryModal = ({
  isOpen,
  onClose,
  onUseTemplate,
}: ITemplateGalleryModalProps): JSX.Element => {
  const styles = useQuickStartGalleryStyles();
  const [selectedTemplate, setSelectedTemplate] = useState<ITemplate | null>(
    null,
  );
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const { data: templatesResponse, isLoading } = useQuery(
    "templates",
    getTemplates,
    {
      refetchOnWindowFocus: false,
      staleTime: QueryCacheConfig.STALE_TIME_DEFAULT,
    },
  );

  const templates = useMemo(
    () =>
      [...(templatesResponse?.templates || [])]
        .filter((template) => template.isActive)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [templatesResponse?.templates],
  );

  useEffect(() => {
    if (isOpen && templates.length > 0 && !selectedTemplate) {
      setSelectedTemplate(templates[0]);
    }
  }, [isOpen, templates, selectedTemplate]);

  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "";
      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
      }
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscKey);
    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [isOpen, onClose]);

  const handleOverlayClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) {
        onClose();
      }
    },
    [onClose],
  );

  const handleUseTemplate = useCallback(() => {
    if (selectedTemplate) {
      onUseTemplate(selectedTemplate);
      onClose();
    }
  }, [selectedTemplate, onUseTemplate, onClose]);

  if (!isOpen) {
    return <></>;
  }

  return (
    <div
      className={mergeClasses(
        styles.modalOverlay,
        isOpen && styles.modalOverlayActive,
      )}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="template-gallery-title"
    >
      <div
        className={mergeClasses(styles.modal, isOpen && styles.modalActive)}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <h3 id="template-gallery-title" className={styles.modalTitle}>
            App Gallery
          </h3>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close gallery"
            type="button"
          >
            <Dismiss24Regular />
          </button>
        </div>

        <p className={styles.modalDescription}>
          This apps gallery provides access to pre-defined content sources to
          accelerate your work and research.
        </p>

        <div className={styles.modalBody}>
          <div className={styles.templateList}>
            <div className={styles.templateListItems}>
              {isLoading ? (
                <>
                  {[...Array(6)].map((_, index) => (
                    <div key={index} className={styles.skeletonListItem}>
                      <div className={styles.skeletonIcon}></div>
                      <div className={styles.skeletonText}></div>
                    </div>
                  ))}
                </>
              ) : (
                templates.map((template: ITemplate) => (
                  <TemplateListItem
                    key={template.id}
                    template={template}
                    isActive={selectedTemplate?.id === template.id}
                    onClick={() => setSelectedTemplate(template)}
                  />
                ))
              )}
            </div>
          </div>

          {isLoading ? (
            <div className={styles.templateDetail}>
              <div className={styles.skeletonLoader}>
                <div
                  className={mergeClasses(
                    styles.skeletonLine,
                    styles.skeletonTitle,
                  )}
                ></div>
                <div
                  className={mergeClasses(
                    styles.skeletonLine,
                    styles.skeletonLine1,
                  )}
                ></div>
                <div
                  className={mergeClasses(
                    styles.skeletonLine,
                    styles.skeletonLine2,
                  )}
                ></div>
                <div
                  className={mergeClasses(
                    styles.skeletonLine,
                    styles.skeletonLine3,
                  )}
                ></div>
                <div
                  className={mergeClasses(
                    styles.skeletonLine,
                    styles.skeletonMeta,
                  )}
                ></div>
                <div
                  className={mergeClasses(
                    styles.skeletonLine,
                    styles.skeletonButton,
                  )}
                ></div>
              </div>
            </div>
          ) : selectedTemplate ? (
            <TemplateDetail template={selectedTemplate} />
          ) : (
            <div className={styles.templateDetail}>
              <p className={styles.templateDetailOverview}>
                Select a template from the list to view details.
              </p>
            </div>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button
            className={styles.useTemplateButton}
            onClick={handleUseTemplate}
            type="button"
            aria-label="Use selected template"
            disabled={!selectedTemplate}
          >
            Use Template
          </button>
        </div>
      </div>
    </div>
  );
};
